import { describeAge, shortAge } from './status'

/**
 * What a room's Blocked section looks like from outside that room.
 *
 * The accent dot and only the accent dot, which already means one thing
 * everywhere in this interface: somebody is waiting on a human. `buttonState`
 * draws it on the room's own Status button, and that is the one room a reader
 * did not need telling about — the blocker they cannot see is the one in the
 * repository they have not opened since Tuesday. So the same dot rides on every
 * list of rooms: the rail on a laptop, the switcher on a phone, which has no
 * rail at all.
 *
 * The age rides with it because a dot alone says "blocked" and a dot with "16d"
 * says "this has been rotting", and those call for different afternoons. It is
 * dimmed rather than coloured for the reason the whole palette is: red and green
 * are semantic here and there is exactly one accent.
 *
 * **It costs about 39px, and a rail row at its default 200px has two to spare.**
 * So a repository name past about twelve characters ellipsises on the rooms
 * that carry this, and only on those. That is the trade and it was taken
 * deliberately: "acme-pric…" beside a mark that says something is waiting is
 * worth more than "acme-pricing" beside nothing, the full name is in the
 * row's own `title`, and the column drags wider. Do not "fix" it by dropping
 * the age — a bare dot costs 16px and still clips the same name, so the width
 * would be spent for a worse signal.
 */
export function BlockedMark({ since }: { since: string | null }) {
  if (!since) return null
  return (
    <span
      title={`Blocked · ${describeAge(since)}`}
      style={{ display: 'flex', alignItems: 'center', gap: 5, flex: 'none' }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)',
          boxShadow: '0 0 0 3px var(--accent-wash)', flex: 'none',
        }}
      />
      <span style={{ font: '400 var(--size-label)/1 var(--font-machine)', color: 'var(--text-meta)' }}>
        {shortAge(since)}
      </span>
    </span>
  )
}
